import dayjs from 'dayjs'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, CartesianGrid, ComposedChart, Label, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Transition } from 'semantic-ui-react'
import { ThemeContext } from 'styled-components'
import CustomIcon from '~/components/CustomIcon'
import InfoPopup from '~/components/InfoPopup'
import { useAuth } from '~/routes/components/AuthProvider'
import { capitalizeFirstLetter, formatNumber, formatNumberWithoutFractions, splitEvery } from '~/utils/helpers'
import { ChartContainer, TooltipWrapper } from '../TradingActivities/style'
import cloneDeep from 'lodash.clonedeep'
import { FixedPriceDataType } from '~/api/services/reporting'

interface FixedPriceChartProps {
  fixedPriceData: FixedPriceDataType
  basePrice: string
  chartDomain: {
    dataMin: number,
    dataMax: number,
    divider: number
  }
  prefixData: {
    title: string
    chartTitle: string
    priceTitle: string
    confirm: string
  }
}

class CustomizedAxisTick extends React.Component {
  render() {
    //@ts-ignore
    const { x, y, stroke, payload, fill } = this.props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor='end' fill={fill} transform='rotate(-35)'>
          {payload.value}
        </text>
      </g>
    );
  }
}

const FixedPriceChart: React.FC<FixedPriceChartProps> = (props) => {
  const { fixedPriceData, chartDomain, prefixData } = props
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)
  const [dataLoading, setDataLoading] = useState(false)
  const array = cloneDeep(fixedPriceData)
  const chartData = array.tradingMonths?.length > 12 ? splitEvery(array.tradingMonths, 12) : [array.tradingMonths]
  const { systemData: { amountUnit, amountVolume, amountPrice } } = useAuth()
  const { firstColor } = useContext(ThemeContext)
  const priceLabel = `${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit}`

  useEffect(() => {
    setActiveIndex(0)
  }, [fixedPriceData])

  useEffect(() => {
    setDataLoading(false)
  }, [activeIndex])

  const data = chartData[activeIndex] ? chartData[activeIndex].map((item) => {
    return {
      ...item,
      price: item.price,
      confirmedAmount: parseInt(item.confirmedAmount),
      basePrice: item.basePrice
    }
  }) : []

  const firstMonth = chartData[activeIndex] && chartData[activeIndex].length !== 0 ?
    dayjs(chartData[activeIndex][0].dateFrom).format('DD.MM.YYYY HH:mm') : ''
  const lastMonth = chartData[activeIndex] && chartData[activeIndex].length !== 0 ?
    dayjs(chartData[activeIndex][chartData[activeIndex].length - 1].dateFrom).format('DD.MM.YYYY HH:mm') : ''

  function valueNaN(item) {
    const notDefinedString = t('Not defined')
    return `${item.name}: ${notDefinedString}`
  }

  function renderValue(item, value) {
    return `${item.name}: ${value} ${item.unit || ''}`
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dateFrom = payload[0]?.payload['dateFrom']
      const uniqueId = payload[0]?.payload['uniqueId']
      return <TooltipWrapper>
        <div style={{ padding: 5 }} key={'date-from'}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{t('Start of trading slot: {{dateFrom}}',
            { dateFrom: dayjs(dateFrom).format('DD.MM.YYYY') })}</p>
        </div>
        <div style={{ padding: 5 }} key={'unique-id'}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{t('Trading slot ID: {{uniqueId}}', { uniqueId })}</p>
        </div>
        {payload.map(item => {
          const value = item.unit && item.unit === amountUnit ? formatNumberWithoutFractions(parseFloat(item.value)) : formatNumber(item.value, 2)
          return <div style={{ padding: 5 }} key={item.name}>
            <p className='tooltipTitle' style={{ color: item.color }}>
              {value === 'NaN' ? valueNaN(item) : renderValue(item, value)}
            </p>
          </div>
        })}
      </TooltipWrapper>
    }

    return null;
  };

  function formatTicks(value) {
    return (parseInt(value) / 1000) < 1 ? `${formatNumberWithoutFractions(parseInt(value))}` :
      `${formatNumberWithoutFractions(parseInt(value) / 1000)}k`
  }

  return <ChartContainer>
    <div className='header'>
      {chartData?.length !== 1 && <div className='arrow-left'>
        <CustomIcon
          onClick={() => {
            setDataLoading(true)
            setActiveIndex(activeIndex - 1)
          }}
          disabled={activeIndex === 0}
          name='arrow alternate circle left'
        />
      </div>
      }
      <div className='title'>
        {`${prefixData.chartTitle} (${firstMonth} - ${lastMonth})`}
        <InfoPopup
          content={t('The first and last date in which a {{title}} for that trading month was offered.', { title: prefixData.priceTitle })}
          styleIcon={{ cursor: 'pointer', fontSize: '1.0em', marginLeft: 5 }}
        />
      </div>
      {chartData?.length !== 1 && <div className='arrow-right'>
        <CustomIcon
          onClick={() => {
            setDataLoading(true)
            setActiveIndex(activeIndex + 1)
          }}
          disabled={activeIndex === chartData?.length - 1}
          name='arrow alternate circle right'
        />
      </div>
      }
    </div>
    <Transition visible={!dataLoading} animation='fade right' duration={250}>
      <ResponsiveContainer>
        <ComposedChart
          data={data}
          maxBarSize={50}
          margin={{
            top: 10,
            right: 30,
            left: 30,
            bottom: 5
          }}
        >
          <CartesianGrid strokeDasharray='1 1' />
          <XAxis
            height={150}
            type={'category'}
            interval={0}
            tick={<CustomizedAxisTick />}
            dataKey='uniqueId'
          />
          <YAxis
            orientation='left'
            yAxisId='left'
            domain={[Math.max(chartDomain.dataMin - chartDomain.divider, 0), chartDomain.dataMax + chartDomain.divider]}
            tickFormatter={(value) => formatNumber(value)}
            dataKey={'price'}
          >
            <Label
              value={t('Price (in {{priceLabel}})', { priceLabel })}
              angle={-90}
              position='insideLeft'
              offset={-5}
              style={{ textAnchor: 'middle' }} />
          </YAxis>
          <YAxis
            yAxisId='right'
            dataKey='confirmedAmount'
            orientation='right'
            tickFormatter={(value) => formatTicks(value)}
          >
            <Label
              value={t('Volume (in {{amountUnit}})', { amountUnit })}
              offset={2}
              position={{
                x: 80,
                y: 60
              }}
              angle={-90} />
          </YAxis>
          <Tooltip content={<CustomTooltip />} animationEasing={'ease-out'} />
          <Bar
            unit={amountUnit}
            yAxisId={'right'}
            dataKey='confirmedAmount'
            name={prefixData.confirm}
            fill='#82ca9d'
          />
          <Line
            yAxisId={'left'}
            type='monotone'
            name={capitalizeFirstLetter(prefixData.priceTitle)}
            dataKey={'price'}
            unit={priceLabel}
            stroke={firstColor}
            dot={{ r: 5 }}
            activeDot={{ r: 8 }}
          />
          <Line
            yAxisId={'left'}
            type='monotone'
            name={props.basePrice}
            unit={priceLabel}
            dataKey={'basePrice'}
            stroke={'black'}
            dot={{ r: 5 }}
            activeDot={{ r: 8 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Transition>
  </ChartContainer>
}
export default FixedPriceChart